import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Link as RouterLink } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { getScrollbarStyles } from 'utils/scrollbarStyles';
import { apiFetcher, apiGet, apiPost, getDisplayErrorMessage } from 'utils/api';

import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import NotificationOutlined from '@ant-design/icons/NotificationOutlined';

const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

function getSeverityStyles(severity) {
  switch (severity) {
    case 'error':
      return { color: 'error.main', bg: 'error.lighter', icon: <WarningOutlined /> };
    case 'warning':
      return { color: 'warning.main', bg: 'warning.lighter', icon: <ClockCircleOutlined /> };
    default:
      return { color: 'primary.main', bg: 'primary.lighter', icon: <NotificationOutlined /> };
  }
}

const groupMeta = {
  new_issues: {
    title: 'New issues',
    detail: 'Fresh findings and tasks that need ownership now.'
  },
  drift: {
    title: 'Drift',
    detail: 'Repeated or newly detected source changes that can reopen controls.'
  },
  stale_evidence: {
    title: 'Stale evidence',
    detail: 'Proof that is aging toward the next review window.'
  },
  workspace_updates: {
    title: 'Workspace updates',
    detail: 'Recent syncs and control updates that are worth knowing about.'
  }
};

const groupOrder = ['new_issues', 'drift', 'stale_evidence', 'workspace_updates'];
const INITIAL_NOTIFICATION_PAGE_SIZE = 12;

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const anchorRef = useRef(null);
  const scrollBodyRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notificationsData, setNotificationsData] = useState(null);
  const { data, error, isLoading, mutate } = useSWR(`/notifications?limit=${INITIAL_NOTIFICATION_PAGE_SIZE}`, apiFetcher);

  useEffect(() => {
    if (data) {
      setNotificationsData(data);
    }
  }, [data]);

  const pageData = notificationsData || data;
  const unreadCount = pageData?.summary?.unreadCount || 0;
  const items = pageData?.items || [];
  const groupedMap = items.reduce((groups, item) => {
      const key = item.group || 'workspace_updates';
      groups[key] = groups[key] || [];
      groups[key].push(item);
      return groups;
    }, {});
  const groupedItems = useMemo(() => {
    return Object.entries(groupedMap)
      .map(([groupKey, groupItems]) => ({
        groupKey,
        groupItems,
        latestCreatedAt: groupItems.reduce((latest, item) => {
          const current = new Date(item.createdAt).getTime();
          return current > latest ? current : latest;
        }, 0)
      }))
      .sort((left, right) => {
        if (right.latestCreatedAt !== left.latestCreatedAt) {
          return right.latestCreatedAt - left.latestCreatedAt;
        }

        return groupOrder.indexOf(left.groupKey) - groupOrder.indexOf(right.groupKey);
      });
  }, [groupedMap]);
  const latestNotificationId = items[0]?.notificationId || '';

  async function loadMoreNotifications() {
    if (loadingMore || !pageData?.paging?.hasMore) {
      return;
    }

    setLoadingMore(true);

    try {
      const response = await apiGet(`/notifications?offset=${pageData.paging.nextOffset || 0}&limit=${pageData.paging.limit || INITIAL_NOTIFICATION_PAGE_SIZE}`);

      setNotificationsData((current) => {
        const base = current || pageData;

        if (!base) {
          return response;
        }

        const seen = new Set(base.items.map((item) => item.notificationId));
        const nextItems = [...base.items];

        (response.items || []).forEach((item) => {
          if (!seen.has(item.notificationId)) {
            nextItems.push(item);
            seen.add(item.notificationId);
          }
        });

        return {
          ...base,
          ...response,
          items: nextItems
        };
      });
    } finally {
      setLoadingMore(false);
    }
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  async function handleMarkAllRead() {
    setMarkingRead(true);

    try {
      await apiPost('/notifications/read');
      const refreshed = await mutate();
      setNotificationsData(refreshed || null);
    } finally {
      setMarkingRead(false);
    }
  }

  const handleScroll = () => {
    const node = scrollBodyRef.current;

    if (!node || loadingMore || !pageData?.paging?.hasMore) {
      return;
    }

    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;

    if (distanceFromBottom < 80) {
      loadMoreNotifications();
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent'
        }}
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 320, maxWidth: { xs: 320, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notifications"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    unreadCount > 0 ? (
                      <Tooltip title="Mark all read">
                        <span>
                          <IconButton
                            color="success"
                            size="small"
                            onClick={handleMarkAllRead}
                            disabled={markingRead}
                            aria-label="Mark all read"
                          >
                            {markingRead ? <CircularProgress size={16} /> : <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null
                  }
                >
                  <Stack spacing={1.25} sx={{ p: 2 }}>
                    <Chip
                      label={unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                      size="small"
                      variant="outlined"
                      sx={{ alignSelf: 'flex-start', borderRadius: '999px' }}
                    />

                    <Box
                      ref={scrollBodyRef}
                      onScroll={handleScroll}
                      sx={(theme) => ({
                        maxHeight: { xs: 360, md: 440 },
                        overflowY: 'auto',
                        pr: 0.75,
                        mr: -0.5,
                        ...getScrollbarStyles(theme, { size: 9 })
                      })}
                    >
                      {isLoading ? (
                        <Typography variant="body2" color="text.secondary">
                          Loading notifications...
                        </Typography>
                      ) : error ? (
                        <Typography variant="body2" color="error.main">
                          {getDisplayErrorMessage(error)}
                        </Typography>
                      ) : items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No notifications yet. New issues, tasks, and prediction signals will show up here.
                        </Typography>
                      ) : (
                        <Stack spacing={1.5}>
                          {groupedItems.map(({ groupKey, groupItems }) => (
                            <Box key={groupKey}>
                              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                                <Box>
                                  <Typography variant="subtitle2">{groupMeta[groupKey]?.title || 'Updates'}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {groupMeta[groupKey]?.detail || 'Recent changes in the workspace.'}
                                  </Typography>
                                </Box>
                                <Chip label={groupItems.length} size="small" variant="outlined" />
                              </Stack>

                              <List
                                component="nav"
                                sx={{
                                  p: 0,
                                  '& .MuiListItemButton-root': {
                                    py: 1,
                                    px: 1.25,
                                    borderRadius: 3,
                                    alignItems: 'flex-start'
                                  }
                                }}
                              >
                                {groupItems.map((item) => {
                                  const styles = getSeverityStyles(item.severity);

                                  return (
                                    <ListItem key={item.notificationId} disablePadding sx={{ mb: 1 }}>
                                      <ListItemButton
                                        component={RouterLink}
                                        to={item.href}
                                        selected={!item.isRead}
                                        onClick={() => setOpen(false)}
                                        sx={{
                                          border: !item.isRead ? '1px solid' : '1px solid transparent',
                                          borderColor: !item.isRead ? 'primary.light' : 'transparent',
                                          backgroundColor: !item.isRead ? 'primary.lighter' : 'transparent'
                                        }}
                                      >
                                        <ListItemAvatar>
                                          <Avatar sx={{ ...avatarSX, color: styles.color, bgcolor: styles.bg }}>{styles.icon}</Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                          primary={
                                            <Stack spacing={0.5}>
                                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                                <Typography variant="subtitle2">{item.title}</Typography>
                                                <Chip label={item.sourceName} size="small" variant="outlined" />
                                                {item.notificationId === latestNotificationId ? (
                                                  <Chip label="Latest" size="small" color="primary" variant="outlined" />
                                                ) : null}
                                                {!item.isRead ? <Chip label="Unread" size="small" color="warning" /> : null}
                                                {item.attentionLabel ? (
                                                  <Chip
                                                    label={item.attentionLabel}
                                                    size="small"
                                                    sx={{ backgroundColor: styles.bg, color: styles.color }}
                                                  />
                                                ) : null}
                                              </Stack>
                                              <Typography variant="body2" color="text.secondary">
                                                {item.detail}
                                              </Typography>
                                            </Stack>
                                          }
                                          secondary={
                                            <Typography variant="caption" color="text.secondary">
                                              {item.createdAtLabel}
                                            </Typography>
                                          }
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  );
                                })}
                              </List>
                            </Box>
                          ))}
                          {pageData?.paging?.hasMore ? (
                            <Box
                              sx={{
                                minHeight: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                                border: '1px dashed',
                                borderColor: 'secondary.light',
                                color: 'text.secondary'
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {loadingMore
                                  ? 'Loading more notifications...'
                                  : `Scroll to load ${pageData.summary.totalCount - (pageData.paging.loadedCount || items.length)} more`}
                              </Typography>
                            </Box>
                          ) : null}
                        </Stack>
                      )}
                    </Box>

                    <Button component={RouterLink} to="/activity" size="small" color="secondary" onClick={() => setOpen(false)}>
                      View activity
                    </Button>
                  </Stack>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
